import React, { useMemo, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { AgentTool, useListScreensQuery, useListToolsQuery } from '@/redux/internal-services/agentsApi';
import type { CardDefinition } from '@/redux/internal-services/agentApi';
import { useCardCatalogue } from '@/components/SAM/moduleTools';
import { AgentFormValues, SectionCard, screenMeta } from './agentShared';
import { canonicalModule, moveWithinSubset } from './promptPlacement';
import { computeToolPlacements, sortToolNames } from './toolPlacement';
import { ToolPicker } from './ScreensTab';
import PromptBlock from './PromptBlock';
import BlockCondition from './BlockCondition';
import LoadTemplateButton from './LoadTemplateButton';
import { describeWhen, humanize, screenConditions } from './screenConditions';
import { Badge } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { Chip } from '@/components/UI/Chip';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';

// A tree: module → card → sub-card (panel). Every row has its own "Load template" (the seeded prompt and tools from the Agent Prompt
// Template Catalogue) and its own "+ Add block". Opening a row shows ONLY that row's blocks:
//   • a card: its tools (tool_scope_overrides.screens[card]) and its card-level blocks (topic_cards: [card]);
//   • a sub-card: its blocks, each with an "Applies when" condition (api-service cards/conditions.py) and its own tools.

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

interface Item {
  idx: number;
  title: string;
  text: string;
  when: string;
  tools: number;
}

const CardsTab: React.FC = () => {
  const { watch, control, setValue } = useFormContext<AgentFormValues>();
  const { data: screens, isLoading } = useListScreensQuery();
  const { data: allTools } = useListToolsQuery();
  const { append, remove, move } = useFieldArray({ control, name: 'system_prompts' });
  const cardCatalogue = useCardCatalogue();
  const [pickedModule, setPickedModule] = useState<string | 'all'>('all');
  const [closedModules, setClosedModules] = useState<Set<string>>(new Set());
  const [openRows, setOpenRows] = useState<Set<string>>(new Set());   // cards and sub-cards start folded

  const watchedOverrides = watch('tool_scope_overrides');
  const overrides = useMemo(
    () => ({ global_tools: watchedOverrides?.global_tools ?? [], screens: watchedOverrides?.screens ?? {}, core_opt_out: watchedOverrides?.core_opt_out ?? [] }),
    [watchedOverrides],
  );
  const watchedTools = watch('tools');
  const selectedTools = useMemo(() => watchedTools || [], [watchedTools]);
  const attachedToolNames = useMemo(
    () => new Set(selectedTools.map((t) => t.name || t.tool_name).filter(Boolean) as string[]),
    [selectedTools],
  );
  const toolCatalogue = useMemo(() => {
    const m = new Map<string, AgentTool>();
    (allTools || []).forEach((t) => { if (t.name) m.set(t.name, t); });
    return m;
  }, [allTools]);
  const coreTools = useMemo(() => screens?.core_tools ?? [], [screens]);
  // Only the tools enabled on this agent (the Tools tab's "Enabled tools" list) can be picked here.
  const availableTools = useMemo(
    () => (screens?.available_tools ?? []).filter((n) => attachedToolNames.has(n)),
    [screens, attachedToolNames],
  );
  const globalToolSet = useMemo(
    () => new Set([...computeToolPlacements(attachedToolNames, overrides, coreTools)].filter(([, p]) => p.isGlobal).map(([n]) => n)),
    [attachedToolNames, overrides, coreTools],
  );

  const watchedSystemPrompts = useWatch({ control, name: 'system_prompts' });
  const cards = useMemo(
    // Admin configuration editors (the workflow structure editor, the company-processes overview) are not scoped per card.
    () => (cardCatalogue ?? []).filter((c) => c.status === 'active' && c.enabled && c.permission !== 'workflow:configure'),
    [cardCatalogue],
  );
  const conditions = useMemo(() => screenConditions(cards), [cards]);
  const cardById = useMemo(() => new Map(cards.map((c) => [c.id, c])), [cards]);

  const cardToolsOf = (cardId: string): string[] =>
    sortToolNames([...new Set(overrides.screens[cardId] ?? [])].filter((n) => attachedToolNames.has(n) && !globalToolSet.has(n)));

  // Blocks by the card or sub-card they belong to (`card` / `card#sub`), each with its position in the agent's own list.
  const byKey = useMemo(() => {
    const out = new Map<string, Item[]>();
    (watchedSystemPrompts ?? []).forEach((b, idx) => {
      if (b.home_module) return;
      let key = (b.topic_cards ?? []).find(Boolean);
      if (!key) {
        const hit = conditions.find((c) => (b.applies_when ?? '').includes(c.expr));
        if (hit) key = hit.expr.startsWith('screen.open(') ? `${hit.card}#${hit.expr.slice("screen.open('".length, -2)}` : hit.card;
      }
      if (!key || !cardById.has(key.split('#')[0])) return;
      out.set(key, [...(out.get(key) ?? []), {
        idx, title: b.title ?? '', text: b.prompt ?? '', when: b.applies_when ?? '', tools: (b.tools ?? []).length,
      }]);
    });
    return out;
  }, [watchedSystemPrompts, conditions, cardById]);

  const itemsOf = (key: string): Item[] => byKey.get(key) ?? [];

  // A newly picked tool is attached to the agent; removing one never detaches it (that stays on the Tools tab).
  const applyAttach = (names: string[]) => {
    const added = names
      .filter((n) => !attachedToolNames.has(n))
      .map((n) => toolCatalogue.get(n))
      .filter((t): t is AgentTool => Boolean(t))
      .map((t) => ({
        tool_id: t.tool_id, name: t.name, tool_name: t.tool_name || t.name, description: t.description,
        requires_confirmation: t.requires_confirmation ?? false, argument_schema: t.argument_schema,
        toolkit_type: t.toolkit_type, toolkit_name: t.toolkit_name, action_card_template: t.action_card_template,
      }));
    if (added.length) setValue('tools', [...selectedTools, ...added], { shouldDirty: true });
  };
  const setKeyTools = (key: string, pending: string[]) => {
    applyAttach(pending);
    const next = { ...overrides.screens };
    if (pending.length > 0) next[key] = pending; else delete next[key];
    setValue('tool_scope_overrides', { ...overrides, screens: next }, { shouldDirty: true });
  };
  // Tools bound straight to a panel key (`card#panel`, the older setup): shown so nothing is hidden.
  const legacyPanelKeys = Object.keys(overrides.screens).filter((k) => k.includes('#') && (overrides.screens[k] ?? []).length > 0);

  const toggleRow = (key: string) => setOpenRows((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const toggleModule = (m: string) => setClosedModules((prev) => {
    const next = new Set(prev);
    if (next.has(m)) next.delete(m); else next.add(m);
    return next;
  });
  const openRow = (key: string) => setOpenRows((prev) => new Set(prev).add(key));

  const newId = () => `blk_${Math.random().toString(36).slice(2, 10)}`;
  /** A new block in this card (`card`) or sub-card (`card#sub`). A sub-card block starts with its panel's condition. */
  const addBlockTo = (key: string) => {
    const [cardId, sub] = key.split('#');
    if (sub) append({ id: newId(), title: '', prompt: '', topic_cards: [key], applies_when: `screen.open('${sub}')`, tools: [] });
    else append({ id: newId(), title: '', prompt: '', topic_cards: [cardId] });
    openRow(key);
  };
  const moveWithin = (subset: number[], pos: number, dir: -1 | 1) => {
    const pair = moveWithinSubset(subset, pos, dir);
    if (pair) move(pair[0], pair[1]);
  };

  const panelsOf = (card: CardDefinition) => (card.components ?? []).map((c) => ({ id: c.id, key: `${card.id}#${c.id}`, label: humanize(c.id) }));
  const panelOptionsOf = (card: CardDefinition) => conditions.filter((c) => c.card === card.id && c.expr.startsWith('screen.open('));

  // Rows to show: every module, card and sub-card (so each has its Load template), narrowed by the search and the module chip.
  const modules = useMemo(() => {
    const out = new Map<string, CardDefinition[]>();
    cards.forEach((c) => {
      const m = canonicalModule(c.module);
      out.set(m, [...(out.get(m) ?? []), c]);
    });
    return out;
  }, [cards]);
  const visibleModules = [...modules].filter(([m]) => pickedModule === 'all' || pickedModule === m);

  const renderBlocks = (items: Item[], panelCard?: CardDefinition) => (
    <div className="space-y-1.5">
      {items.map((it, pos) => (
        <PromptBlock
          key={it.idx}
          index={it.idx}
          total={items.length}
          onMove={(dir) => moveWithin(items.map((s) => s.idx), pos, dir)}
          onRemove={() => remove(it.idx)}
          showScreenPicker={false}
          slot={panelCard ? (
            <BlockCondition
              index={it.idx}
              options={panelOptionsOf(panelCard)}
              describe={describeWhen(it.when, conditions)}
              toolNames={availableTools}
              globalNames={globalToolSet}
              onAttach={applyAttach}
            />
          ) : undefined}
        />
      ))}
    </div>
  );

  return (
    <SectionCard title="Screen blocks">
      {isLoading && <LoadingState label="Loading cards…" className="py-6" />}
      {!isLoading && cards.length === 0 && <EmptyState variant="inline" title="No cards available." />}

      {cards.length > 0 && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <nav aria-label="Filter by module" className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
              <Chip selected={pickedModule === 'all'} onClick={() => setPickedModule('all')}>All</Chip>
              {[...modules.keys()].map((m) => (
                <Chip key={m} selected={pickedModule === m} onClick={() => setPickedModule(m)}>{screenMeta(m).label}</Chip>
              ))}
            </nav>
          </div>

          {visibleModules.map(([module, moduleCards]) => {
            const shownCards = moduleCards;
            const moduleOpen = !closedModules.has(module);
            return (
              <section key={module} aria-label={screenMeta(module).label} className="space-y-2 rounded-card border border-ui-border3 bg-ui p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => toggleModule(module)}
                    aria-expanded={moduleOpen}
                    className="flex min-w-0 flex-1 items-center gap-2 rounded-control px-1 py-0.5 text-left hover:bg-ui-hover focus-ring"
                  >
                    <span className="w-3 shrink-0 text-xs text-ui-text3">{moduleOpen ? '▾' : '▸'}</span>
                    <h3 className="text-sm font-semibold text-ui-text">{screenMeta(module).label}</h3>
                    <span className="text-xs text-ui-text3">{plural(shownCards.length, 'card')}</span>
                  </button>
                </div>

                {moduleOpen && shownCards.map((card) => {
                  const cardKey = card.id;
                  const open = openRows.has(cardKey);
                  const cardItems = itemsOf(cardKey);
                  const panels = panelsOf(card);
                  const cardTools = cardToolsOf(card.id);
                  const total = (byKey.get(cardKey) ?? []).length + panels.reduce((n, p) => n + (byKey.get(p.key) ?? []).length, 0);
                  return (
                    <div key={card.id} className="overflow-hidden rounded-control border border-ui-border bg-ui-card">
                      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                        <button
                          type="button"
                          onClick={() => toggleRow(cardKey)}
                          aria-expanded={open}
                          className="flex min-w-0 flex-1 items-center gap-2 text-left focus-ring"
                        >
                          <span className="w-3 shrink-0 text-xs text-ui-text3">{open ? '▾' : '▸'}</span>
                          <span className="font-mono text-sm font-medium text-ui-text">{card.id}</span>
                          <Badge variant="neutral">{card.presentation}</Badge>
                          <span className="min-w-0 flex-1 truncate text-xs text-ui-text3">{card.description}</span>
                          <Badge variant={total > 0 ? 'accent' : 'neutral'} title="Blocks on this card and its sub-cards">{plural(total, 'block')}</Badge>
                        </button>
                        <LoadTemplateButton scope="card" templateKey={cardKey} />
                      </div>

                      {open && (
                        <div className="space-y-2 border-t border-ui-border p-3">
                          <div>
                            <span className="text-xs text-ui-text2">Card tools <span className="text-ui-text3">({cardTools.length}) — offered while this card is open</span></span>
                            <ToolPicker
                              names={availableTools}
                              selected={cardTools}
                              globalNames={globalToolSet}
                              onChange={(pending) => setKeyTools(card.id, pending)}
                              label="+ add tools to this card"
                              emptyText="No tools assigned."
                            />
                          </div>
                          {cardItems.length > 0 && renderBlocks(cardItems)}
                          <div className="flex justify-end">
                            <Button type="button" variant="soft" size="xs" onClick={() => addBlockTo(cardKey)}>+ Add block</Button>
                          </div>

                          {panels.length > 0 && (
                            <div className="space-y-1.5 border-t border-ui-border pt-2">
                              <p className="text-xs font-medium text-ui-text2">Sub-cards</p>
                              {panels.map((p) => {
                                const items = itemsOf(p.key);
                                const all = byKey.get(p.key) ?? [];
                                const pOpen = openRows.has(p.key);
                                const toolCount = all.reduce((n, i) => n + i.tools, 0);
                                return (
                                  <div key={p.key} className="overflow-hidden rounded-control border border-ui-border bg-ui-subtle">
                                    <div className="flex flex-wrap items-center gap-2 px-3 py-1.5">
                                      <button
                                        type="button"
                                        onClick={() => toggleRow(p.key)}
                                        aria-expanded={pOpen}
                                        className="flex min-w-0 flex-1 items-center gap-2 text-left focus-ring"
                                      >
                                        <span className="w-3 shrink-0 text-xs text-ui-text3">{pOpen ? '▾' : '▸'}</span>
                                        <span className="text-xs font-medium text-ui-text">{p.label}</span>
                                        <Badge variant="neutral">sub-card</Badge>
                                        <Badge variant={all.length > 0 ? 'accent' : 'neutral'}>
                                          {plural(all.length, 'block')}{toolCount > 0 ? ` · ${plural(toolCount, 'tool')}` : ''}
                                        </Badge>
                                      </button>
                                      <LoadTemplateButton scope="subcard" templateKey={p.key} />
                                    </div>
                                    {pOpen && (
                                      <div className="space-y-2 border-t border-ui-border p-3">
                                        {items.length > 0 && renderBlocks(items, card)}
                                        <div className="flex justify-end">
                                          <Button type="button" variant="soft" size="xs" onClick={() => addBlockTo(p.key)}>+ Add block</Button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </section>
            );
          })}

          {legacyPanelKeys.length > 0 && (
            <div className="space-y-2 rounded-control border border-ui-border bg-ui-subtle px-3 py-2">
              <p className="text-xs text-ui-text2">
                Tools bound straight to a sub-card <span className="text-ui-text3">(older setup — move them onto a sub-card block to give them a condition)</span>
              </p>
              {legacyPanelKeys.map((k) => (
                <div key={k}>
                  <span className="font-mono text-xs text-ui-text">{k}</span>
                  <ToolPicker
                    names={availableTools}
                    selected={sortToolNames([...new Set(overrides.screens[k] ?? [])].filter((n) => attachedToolNames.has(n) && !globalToolSet.has(n)))}
                    globalNames={globalToolSet}
                    onChange={(pending) => setKeyTools(k, pending)}
                    label={`+ add tools to ${k}`}
                    emptyText="No tools assigned."
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
};

export default CardsTab;
